// dashboard-agent-ia/src/pages/Register.js
import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import axios from 'axios'
import { MdSmartToy, MdCheck, MdArrowForward, MdWhatsapp, MdAutoAwesome, MdLanguage, MdAccessTime, MdLock } from 'react-icons/md'
import PhoneInput, { countries } from '../components/PhoneInput'
import PinInput from '../components/PinInput'
import LangSelector from '../components/LangSelector'
import { useLang } from '../context/LangContext'
import { validatePin } from '../utils/pinValidation'

const API_URL = process.env.REACT_APP_API_URL || 'https://agent-ia-senegal.onrender.com'

const tr = {
  fr: {
    tagline: 'Powered by AI · Made for the World 🌍',
    hero_title: 'Votre assistant', hero_highlight: 'WhatsApp intelligent', hero_subtitle: 'répond à votre place',
    hero_desc: 'Offrez à vos clients une expérience exceptionnelle — des réponses instantanées en wolof, français et anglais, disponibles 24h/24 et 7j/7.',
    features: [
      { title: 'WhatsApp natif', desc: 'Vos clients écrivent sur votre numéro habituel' },
      { title: 'Multilingue', desc: "L'IA détecte et répond dans la langue du client" },
      { title: '24h/24 — 7j/7', desc: 'Jamais de message sans réponse, même la nuit' },
      { title: 'IA intelligente', desc: 'Se souvient de chaque client et personnalise les réponses' },
    ],
    social_proof: 'Rejoignez les entreprises qui font confiance à notre IA',
    step1_label: 'Étape 1 sur 3', step1_title: 'Votre business', step1_sub: 'Configuration en moins de 3 minutes',
    name_label: 'Nom de votre entreprise', name_placeholder: 'Ex: Restaurant Chez Aminata',
    phone_label: 'Numéro WhatsApp Business', sector_label: "Secteur d'activité",
    step2_label: 'Étape 2 sur 5', step2_title: 'Choisissez votre plan', step2_sub: "Commencez dès aujourd'hui",
    step3_label: 'Étape 3 sur 5', step3_title: 'Configurez votre IA', step3_sub: "Ces informations permettent à l'IA de répondre parfaitement",
    prompt_preview: '🤖 Aperçu de votre agent IA',
    info_label: "Informations pour votre agent", info_sub: '(menu, horaires, services, prix...)',
    step4_label: 'Étape 2 sur 3', step4_title: 'Créez votre PIN secret',
    step4_sub: 'Ce code à 6 chiffres protège votre compte. Ne le partagez jamais.',
    pin_label: 'Choisissez votre PIN à 6 chiffres', pin_confirm_label: 'Confirmez votre PIN',
    pin_mismatch: 'Les deux PIN ne correspondent pas',
    step5_label: 'Étape 3 sur 3', step5_title: 'Vérification WhatsApp',
    step5_sub: 'Un code à 6 chiffres a été envoyé sur votre WhatsApp',
    otp_label: 'Code de vérification reçu sur WhatsApp',
    continue: 'Continuer', next_btn: 'Continuer',
    already: 'Déjà inscrit ?', signin: 'Se connecter',
    back: 'Retour', launch: '🚀 Vérifier et lancer', launching: 'Vérification...',
    per_month: '/mois', popular: '🔥 Populaire', complete: '⭐ Complet', selected: '✓ Sélectionné',
    phone_error: 'Numéro invalide', phone_preview: 'Numéro enregistré',
    company: 'Entreprise', phone_field: 'WhatsApp', sector_field: 'Secteur', plan_field: 'Plan', country_field: 'Pays',
    success_title: 'Bienvenue à bord ! 🎉',
    success_sub: 'Votre agent IA est maintenant actif pour',
    success_contact: 'Connectez-vous pour accéder à votre dashboard.',
    go_dashboard: 'Accéder au dashboard',
    error_otp: 'Code incorrect ou expiré',
    error_general: 'Une erreur est survenue. Réessayez.',
    resend: 'Renvoyer le code',
    prompt_base: (name, country) => `Tu es l'assistant WhatsApp de "${name}" (${country}). Réponds toujours en wolof, français ou anglais selon la langue du client. Sois professionnel et chaleureux.`,
    plans: [
      { id: 'starter', name: 'Starter', price: '15 000', features: ['500 messages/mois', '1 langue', 'Support email', 'Dashboard inclus'] },
      { id: 'pro', name: 'Pro', price: '30 000', badge: '🔥 Populaire', features: ['2000 messages/mois', 'Wolof + Français + Anglais', 'Réponses vocales', 'Statistiques'] },
      { id: 'premium', name: 'Premium', price: '50 000', badge: '⭐ Complet', features: ['Messages illimités', 'Toutes les langues', 'Analytics', 'Support prioritaire'] },
    ],
  },
  en: {
    tagline: 'Powered by AI · Made for the World 🌍',
    hero_title: 'Your intelligent', hero_highlight: 'WhatsApp assistant', hero_subtitle: 'responds for you',
    hero_desc: 'Give your customers an exceptional experience — instant responses in Wolof, French and English, available 24/7.',
    features: [
      { title: 'Native WhatsApp', desc: 'Your clients write on your usual number' },
      { title: 'Multilingual', desc: "AI detects and replies in the client's language" },
      { title: '24/7', desc: 'Never a message without a response' },
      { title: 'Smart AI', desc: 'Remembers each client and personalizes responses' },
    ],
    social_proof: 'Join businesses that trust our AI',
    step1_label: 'Step 1 of 3', step1_title: 'Your business', step1_sub: 'Setup in less than 3 minutes',
    name_label: 'Business name', name_placeholder: 'Ex: Aminata Restaurant',
    phone_label: 'WhatsApp Business number', sector_label: 'Business sector',
    step2_label: 'Step 2 of 5', step2_title: 'Choose your plan', step2_sub: 'Start today — cancel anytime',
    step3_label: 'Step 3 of 5', step3_title: 'Configure your AI', step3_sub: 'This information allows the AI to respond perfectly',
    prompt_preview: '🤖 Your AI agent preview',
    info_label: 'Information for your agent', info_sub: '(menu, hours, services, prices...)',
    step4_label: 'Step 2 of 3', step4_title: 'Create your secret PIN',
    step4_sub: 'This 6-digit code protects your account. Never share it.',
    pin_label: 'Choose your 6-digit PIN', pin_confirm_label: 'Confirm your PIN',
    pin_mismatch: 'PINs do not match',
    step5_label: 'Step 3 of 3', step5_title: 'WhatsApp Verification',
    step5_sub: 'A 6-digit code was sent to your WhatsApp',
    otp_label: 'Verification code received on WhatsApp',
    continue: 'Next', next_btn: 'Next',
    already: 'Already registered?', signin: 'Sign in',
    back: 'Back', launch: '🚀 Verify and launch', launching: 'Verifying...',
    per_month: '/month', popular: '🔥 Popular', complete: '⭐ Complete', selected: '✓ Selected',
    phone_error: 'Invalid number', phone_preview: 'Stored number',
    company: 'Business', phone_field: 'WhatsApp', sector_field: 'Sector', plan_field: 'Plan', country_field: 'Country',
    success_title: 'Welcome aboard! 🎉',
    success_sub: 'Your AI agent is now active for',
    success_contact: 'Log in to access your dashboard.',
    go_dashboard: 'Go to dashboard',
    error_otp: 'Incorrect or expired code',
    error_general: 'An error occurred. Please try again.',
    resend: 'Resend code',
    prompt_base: (name, country) => `You are the WhatsApp assistant of "${name}" (${country}). Always reply in Wolof, French or English depending on the client's language. Be professional and friendly.`,
    plans: [
      { id: 'starter', name: 'Starter', price: '15 000', features: ['500 messages/month', '1 language', 'Email support', 'Dashboard included'] },
      { id: 'pro', name: 'Pro', price: '30 000', badge: '🔥 Popular', features: ['2000 messages/month', 'Wolof + French + English', 'Voice replies', 'Statistics'] },
      { id: 'premium', name: 'Premium', price: '50 000', badge: '⭐ Complete', features: ['Unlimited messages', 'All languages', 'Analytics', 'Priority support'] },
    ],
  },
  wo: {
    tagline: 'Powered by AI · Made for the World 🌍',
    hero_title: 'Sa asistan', hero_highlight: 'WhatsApp bu xam-xam', hero_subtitle: 'dafa tontu ci sa xanaam',
    hero_desc: 'Jox sa yëngu-yëngu benn tawfeex bu baax — tontu yëgël ci wolof, français ak anglais, 24h/24.',
    features: [
      { title: 'WhatsApp bu dëgg', desc: 'Yëngu-yëngu yi bind ci sa nimeero' },
      { title: 'Làkk yu bari', desc: 'IA bi xam làkk bi di jëfandikoo' },
      { title: '24h/24 — 7j/7', desc: 'Dara message du dem sans tontu' },
      { title: 'IA bu xam-xam', desc: 'Xamal nit ku nekk ak tontu bu baax' },
    ],
    social_proof: 'Bokk ak entreprise yi di jëfandikoo IA bi',
    step1_label: 'Étape 1 ci 3', step1_title: 'Sa entreprise', step1_sub: 'Configuration ci tuki 3 minit',
    name_label: 'Tur bi sa entreprise', name_placeholder: 'Ex: Restaurant Chez Aminata',
    phone_label: 'Sa nimeero WhatsApp Business', sector_label: 'Domaine bi',
    step2_label: 'Étape 2 ci 5', step2_title: 'Tann sa plan', step2_sub: 'Tàmbalee tey',
    step3_label: 'Étape 3 ci 5', step3_title: 'Configure sa IA', step3_sub: 'Xibaar yi dina wax IA bi tontu bu baax',
    prompt_preview: '🤖 Xool agent IA bi',
    info_label: 'Xibaar yu njëkk ak IA bi', info_sub: '(menu, heures, services, prix...)',
    step4_label: 'Étape 2 ci 3', step4_title: 'Sëtu sa code PIN',
    step4_sub: 'Code bi ak 6 nimeero bi dafa bëgg sa compte.',
    pin_label: 'Tann sa PIN ak 6 nimeero', pin_confirm_label: 'Seetlu sa PIN',
    pin_mismatch: 'PIN yi du bokk',
    step5_label: 'Étape 3 ci 3', step5_title: 'Seetlu WhatsApp',
    step5_sub: 'Code bi ak 6 nimeero yónnee na ci sa WhatsApp',
    otp_label: 'Code bi njëkk ci sa WhatsApp',
    continue: 'Dem kanam', next_btn: 'Dem kanam',
    already: 'Déjà inscrit?', signin: 'Dugg',
    back: 'Déllu', launch: '🚀 Seetlu ak lancer', launching: 'Seetlu...',
    per_month: '/wëlde', popular: '🔥 Bu bari', complete: '⭐ Yëgël', selected: '✓ Tann na',
    phone_error: 'Nimeero bu baax', phone_preview: 'Nimeero bu enregistré',
    company: 'Entreprise', phone_field: 'WhatsApp', sector_field: 'Domaine', plan_field: 'Plan', country_field: 'Réew',
    success_title: 'Dalal ak jàmm ! 🎉',
    success_sub: 'Sa agent IA actif na ci',
    success_contact: 'Dugg ci sa dashboard.',
    go_dashboard: 'Dem dashboard bi',
    error_otp: 'Code bu baax walla jeex na',
    error_general: 'Am na njëkk. Jëfandikoo tuuti.',
    resend: 'Yónn code bi tékki',
    prompt_base: (name, country) => `Yëgël-yëgël sa yëngu-yëngu ci WhatsApp "${name}" (${country}). Tontu ci wolof, français walla anglais. Jëf ak sañ-sañ ak yëgël.`,
    plans: [
      { id: 'starter', name: 'Starter', price: '15 000', features: ['500 messages/wëlde', 'Làkk 1', 'Support email', 'Dashboard'] },
      { id: 'pro', name: 'Pro', price: '30 000', badge: '🔥 Bu bari', features: ['2000 messages/wëlde', 'Wolof + Français + Anglais', 'Tontu vocal', 'Statistiques'] },
      { id: 'premium', name: 'Premium', price: '50 000', badge: '⭐ Yëgël', features: ['Messages yëp', 'Làkk yëp', 'Analytics', 'Support bu njëkk'] },
    ],
  }
}

const cleanDigits = (raw) => raw.replace(/\D/g, '')
const formatPhoneDB = (raw, country) => `${country.dial}${cleanDigits(raw)}`
const validatePhone = (raw, country) => cleanDigits(raw).length === country.digits
const capitalizeWords = (str) => {
  if (!str) return ''
  return str.split(' ').map(word => {
    if (!word) return word
    const chars = [...word]
    return chars[0].toUpperCase() + chars.slice(1).join('').toLowerCase()
  }).join(' ')
}
const steps = 5 // eslint-disable-line no-unused-vars

export default function Register() {
  const navigate = useNavigate()
  const { lang } = useLang()
  const t = tr[lang] || tr.fr
  const [step, setStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [pinError, setPinError] = useState('')
  const [selectedCountry, setSelectedCountry] = useState(countries[0])
  const [rawPhone, setRawPhone] = useState('')
  const [pin, setPin] = useState('')
  const [pinConfirm, setPinConfirm] = useState('')
  const [otp, setOtp] = useState('')
  const [form, setForm] = useState({ name: '', sector: '', plan: 'pro', systemPrompt: '', customInfo: '' })
  const [sectorsMap, setSectorsMap] = useState({})
  const [regSectorMain, setRegSectorMain] = useState('')

  // Charger secteurs depuis API
  React.useEffect(() => {
    fetch(API_URL + '/api/sectors?country=' + (selectedCountry?.code || 'SN'))
      .then(r => r.json())
      .then(data => {
        const map = {}
        ;(data || []).forEach(s => { map[s.label] = s.subcategories || [] })
        setSectorsMap(map)
      })
      .catch(() => {})
  }, [selectedCountry?.code]) // eslint-disable-line react-hooks/exhaustive-deps

  const fullPhone = formatPhoneDB(rawPhone, selectedCountry)

  const getPrompt = () =>
    t.prompt_base(form.name || 'notre entreprise', selectedCountry.name) +
    (form.customInfo ? `\n\nInformations importantes :\n${form.customInfo}` : '')

  const handlePhoneChange = (e) => {
    const val = e.target.value.replace(/[^\d\s]/g, '')
    if (cleanDigits(val).length > selectedCountry.digits) return
    setRawPhone(val); setPhoneError('')
  }

  const handlePinChange = (val) => {
    setPin(val)
    if (val.length === 6) {
      const err = validatePin(val, cleanDigits(rawPhone), lang)
      setPinError(err || '')
    } else setPinError('')
  }

  const handleNext = () => {
    setError('')
    if (step === 0) {
      if (!validatePhone(rawPhone, selectedCountry)) {
        setPhoneError(`${t.phone_error} (${selectedCountry.digits} chiffres)`)
        return
      }
      setPhoneError('')
    }
    if (step === 1) {
      const pinErr = validatePin(pin, cleanDigits(rawPhone), lang)
      if (pinErr) { setPinError(pinErr); return }
      if (pin !== pinConfirm) { setError(t.pin_mismatch); return }
      sendOTP(); return
    }
    // étapes 2 et 3 supprimées
    setStep(s => s + 1)
  }

  const sendOTP = async () => {
    setLoading(true)
    try {
      await axios.post(`${API_URL}/api/auth/register`, {
        phone: fullPhone, pin,
        businessName: form.name, sector: form.sector,
        plan: form.plan, systemPrompt: form.systemPrompt || getPrompt()
      })
      setStep(2)
    } catch (e) {
      setError(e.response?.data?.error || t.error_general)
    } finally { setLoading(false) }
  }

  const handleVerifyOTP = async () => {
    setLoading(true); setError('')
    try {
      await axios.post(`${API_URL}/api/auth/verify-otp`, { phone: fullPhone, otp })
      setStep(3)
    } catch (e) {
      setError(e.response?.data?.error || t.error_otp)
    } finally { setLoading(false) }
  }

  const ProgressBar = ({ white = false }) => (
    <div className="flex gap-2 mb-6">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= step ? 'bg-blue-500' : white ? 'bg-white/20' : 'bg-gray-100'}`} />
      ))}
    </div>
  )

  // Succès
  if (step === 3) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-10 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <MdCheck size={40} className="text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">{t.success_title}</h2>
          <p className="text-gray-500 mb-2">{t.success_sub}</p>
          <p className="text-blue-600 font-bold text-lg mb-4">{form.name}</p>
          <p className="text-gray-400 text-sm mb-6">{t.success_contact}</p>
          <button onClick={() => navigate('/login')} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl">
            {t.go_dashboard}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">

      {/* ÉTAPE 1 */}
      {step === 0 && (
        <div className="min-h-screen flex flex-col lg:flex-row">
          <div className="lg:w-1/2 flex flex-col justify-center p-10 lg:p-16">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                  <MdSmartToy size={28} className="text-white" />
                </div>
                <div>
                  <p className="text-white font-bold text-xl">Agent IA</p>
                  <p className="text-blue-400 text-sm">{t.tagline}</p>
                </div>
              </div>
            </div>
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
              {t.hero_title}<br /><span className="text-blue-400">{t.hero_highlight}</span><br />{t.hero_subtitle}
            </h1>
            <p className="text-slate-300 text-lg mb-10 leading-relaxed">{t.hero_desc}</p>
            <div className="grid grid-cols-1 gap-4">
              {t.features.map((f, i) => (
                <div key={i} className="flex items-center gap-4 bg-white/5 rounded-2xl p-4 border border-white/10">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center text-blue-400 flex-shrink-0">
                    {i === 0 ? <MdWhatsapp size={22}/> : i === 1 ? <MdLanguage size={22}/> : i === 2 ? <MdAccessTime size={22}/> : <MdAutoAwesome size={22}/>}
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">{f.title}</p>
                    <p className="text-slate-400 text-sm">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:w-1/2 flex items-center justify-center p-5 sm:p-8 lg:p-12">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8">
              {/* LangSelector centré */}
              <div className="flex justify-center mb-6">
                <LangSelector center dark={false} />
              </div>
              <ProgressBar />
              <p className="text-sm text-blue-600 font-medium mb-1">{t.step1_label}</p>
              <h2 className="text-2xl font-bold text-gray-800 mb-1">{t.step1_title}</h2>
              <p className="text-gray-500 mb-6">{t.step1_sub}</p>
              {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">{error}</div>}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.name_label}</label>
                  <input type="text" value={form.name}
                    onChange={e => setForm({...form, name: capitalizeWords(e.target.value)})}
                    className="w-full px-4 py-3 border-2 border-blue-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-300 text-gray-800"
                    placeholder={t.name_placeholder} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.phone_label}</label>
                  <PhoneInput value={rawPhone} onChange={handlePhoneChange}
                    selectedCountry={selectedCountry} setSelectedCountry={setSelectedCountry}
                    onCountryChange={() => { setRawPhone(''); setPhoneError('') }}
                    error={phoneError} phonePreviewLabel={t.phone_preview} checkExists={true} />
                  <p className="text-xs mt-1 text-amber-600 flex items-center gap-1">
                    <span>⚠️</span> Assurez-vous que ce numéro est bien actif sur WhatsApp — vous recevrez votre code de vérification dessus.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.sector_label}</label>
                  <select value={regSectorMain}
                    onChange={e => { setRegSectorMain(e.target.value); setForm({...form, sector: e.target.value}) }}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 text-gray-800 mb-2">
                    <option value="">-- Choisissez votre secteur --</option>
                    {Object.keys(sectorsMap).sort((a,b) => a.localeCompare(b,'fr')).map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                    <option value="Autre">Autre (précisez ci-dessous)</option>
                  </select>
                  {regSectorMain && sectorsMap[regSectorMain] && (
                    <select value={form.sector.includes(' — ') ? form.sector.split(' — ')[1] : ''}
                      onChange={e => setForm({...form, sector: e.target.value ? regSectorMain + ' — ' + e.target.value : regSectorMain})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 text-gray-800">
                      <option value="">-- Précisez votre activité --</option>
                      {[...(sectorsMap[regSectorMain] || [])].sort((a,b) => a.localeCompare(b,'fr')).map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  )}
                  {regSectorMain === 'Autre' && (
                    <input value={form.sector === 'Autre' ? '' : form.sector}
                      onChange={e => setForm({...form, sector: e.target.value})}
                      placeholder="Décrivez votre secteur d'activité"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 text-gray-800 mt-2"/>
                  )}
                </div>
              </div>
              <button onClick={handleNext} disabled={!form.name || !rawPhone || !form.sector}
                className="w-full mt-6 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2">
                {t.next_btn} <MdArrowForward size={20} />
              </button>
              <p className="text-center text-gray-400 text-sm mt-4">
                {t.already} <Link to="/login" className="text-blue-600 hover:underline">{t.signin}</Link>
              </p>
            </div>
          </div>
        </div>
      )}


      {/* ÉTAPE 4 — PIN */}
      {step === 1 && (
        <div className="min-h-screen flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <div className="flex justify-center mb-4"><LangSelector center /></div>
            <div className="text-center mb-6">
              <p className="text-blue-400 font-medium mb-2">{t.step4_label}</p>
              <h2 className="text-3xl font-bold text-white mb-3">{t.step4_title}</h2>
              <p className="text-slate-400 text-sm">{t.step4_sub}</p>
            </div>
            <ProgressBar white />
            <div className="bg-white rounded-3xl p-8 shadow-2xl">
              <div className="flex justify-center mb-6">
                <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <MdLock size={30} className="text-blue-600"/>
                </div>
              </div>
              {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm text-center">{error}</div>}
              <div className="space-y-6">
                <PinInput value={pin} onChange={handlePinChange} label={t.pin_label} id="reg-pin" autoFocus error={pinError} />
                <PinInput value={pinConfirm} onChange={setPinConfirm} label={t.pin_confirm_label} id="reg-pinc" />
              </div>
              <div className="flex gap-4 mt-6">
                <button onClick={() => setStep(0)} className="flex-1 py-3 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50">{t.back}</button>
                <button onClick={handleNext} disabled={loading || pin.length !== 6 || pinConfirm.length !== 6 || !!pinError}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2">
                  {loading ? '...' : <>{t.next_btn} <MdArrowForward size={20}/></>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ÉTAPE 5 — OTP */}
      {step === 2 && (
        <div className="min-h-screen flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <div className="flex justify-center mb-4"><LangSelector center /></div>
            <div className="text-center mb-6">
              <p className="text-blue-400 font-medium mb-2">{t.step5_label}</p>
              <h2 className="text-3xl font-bold text-white mb-3">{t.step5_title}</h2>
              <p className="text-slate-400 text-sm">{t.step5_sub}</p>
              <p className="text-blue-400 font-semibold mt-1">{fullPhone}</p>
            </div>
            <ProgressBar white />
            <div className="bg-white rounded-3xl p-8 shadow-2xl">
              <div className="flex justify-center mb-4">
                <MdWhatsapp size={48} className="text-green-500"/>
              </div>
              {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm text-center">{error}</div>}
              <PinInput value={otp} onChange={setOtp} label={t.otp_label} id="otp" autoFocus />
              <button onClick={handleVerifyOTP} disabled={loading || otp.length !== 6}
                className="w-full mt-6 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2">
                {loading ? t.launching : t.launch}
              </button>
              <button onClick={() => { setOtp(''); sendOTP() }} className="w-full mt-3 text-gray-400 hover:text-gray-600 text-sm py-2">
                {t.resend}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
